import React, { useEffect, useState, useCallback } from "react";
import { Mail, BellRing, MessageSquare, Save, Loader2, MailPlus, Send, Eye, EyeOff } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import LicenseManagement from "@/pages/admin/LicenseManagement";
import CollapsibleCard from "@/components/CollapsibleCard";
import FeatureSwitches from "@/pages/admin/FeatureSwitches";

function ToggleCard({ icon: Icon, title, description, value, onToggle, disabled, statusOn, statusOff, note, testId }) {
  return (
    <Card className="max-w-2xl border-slate-200 p-6 shadow-sm" data-testid={`settings-${testId}-card`}>
      <div className="flex items-start justify-between gap-6">
        <div className="flex gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
            <Icon className="h-5 w-5" />
          </span>
          <div>
            <Label className="text-base font-semibold text-slate-800">{title}</Label>
            <p className="mt-1 text-sm text-slate-500">{description}</p>
            {note && <p className="mt-2 text-xs font-medium text-amber-600">{note}</p>}
          </div>
        </div>
        <Switch checked={value} disabled={disabled} onCheckedChange={onToggle} data-testid={`settings-${testId}-toggle`} />
      </div>
      <p className="mt-4 text-xs font-semibold uppercase tracking-wide" data-testid={`settings-${testId}-status`}>
        <span className={value ? "text-emerald-600" : "text-slate-400"}>● {value ? statusOn : statusOff}</span>
      </p>
    </Card>
  );
}

function WelcomeEmailCard() {
  const [wel, setWel] = useState({ subject: "", body: "", enabled: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/coach-welcome-email");
      setWel(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/coach-welcome-email", wel);
      setWel(data);
      toast.success("Welcome email saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  // Sample values used only for the preview — mirrors the backend template rendering.
  const SAMPLE = { first_name: "Jordan", last_name: "Smith", login_url: `${window.location.origin}/login` };
  const previewText = (wel.body || "")
    .replaceAll("{first_name}", SAMPLE.first_name)
    .replaceAll("{last_name}", SAMPLE.last_name)
    .replaceAll("{login_url}", SAMPLE.login_url);
  const previewSubject = (wel.subject || "")
    .replaceAll("{first_name}", SAMPLE.first_name)
    .replaceAll("{last_name}", SAMPLE.last_name);
  const previewParts = previewText.split(SAMPLE.login_url);

  return (
    <CollapsibleCard title="New coach welcome email" icon={MailPlus} testId="settings-coach-welcome-card">
      <div className="mb-4 flex items-start justify-between gap-6">
        <p className="text-sm text-slate-500">
          Automatically emailed to a coach when you create their account on the Users page.
          Requires Email notifications (above) to be on.
        </p>
        <Switch
          checked={!!wel.enabled}
          disabled={loading || saving}
          onCheckedChange={(v) => setWel((w) => ({ ...w, enabled: v }))}
          data-testid="settings-coach-welcome-toggle"
        />
      </div>

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Subject</Label>
          <Input
            value={wel.subject}
            onChange={(e) => setWel((w) => ({ ...w, subject: e.target.value }))}
            disabled={loading}
            data-testid="settings-coach-welcome-subject"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Message</Label>
          <Textarea
            value={wel.body}
            onChange={(e) => setWel((w) => ({ ...w, body: e.target.value }))}
            disabled={loading}
            rows={16}
            className="font-mono text-sm leading-relaxed"
            data-testid="settings-coach-welcome-body"
          />
          <p className="text-xs text-slate-400">
            Use <code className="rounded bg-slate-100 px-1">{"{first_name}"}</code>,{" "}
            <code className="rounded bg-slate-100 px-1">{"{last_name}"}</code> and{" "}
            <code className="rounded bg-slate-100 px-1">{"{login_url}"}</code> — they're replaced with the coach's
            name and the login link when the email is sent.
          </p>
        </div>
        <div className="flex justify-end gap-2">
          <Button onClick={() => setShowPreview((s) => !s)} variant="outline" disabled={loading} className="rounded-full" data-testid="settings-coach-welcome-preview-toggle">
            {showPreview ? <EyeOff className="mr-2 h-4 w-4" /> : <Eye className="mr-2 h-4 w-4" />} {showPreview ? "Hide preview" : "Preview"}
          </Button>
          <Button onClick={save} disabled={loading || saving} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="settings-coach-welcome-save">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save welcome email
          </Button>
        </div>

        {showPreview && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="settings-coach-welcome-preview">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Preview — what a new coach receives</p>
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 px-5 py-3 text-sm">
                <p className="text-slate-500">To: <span className="font-medium text-slate-700">jordan.smith@example.com</span></p>
                <p className="mt-0.5 text-slate-500">Subject: <span className="font-semibold text-slate-900" data-testid="settings-coach-welcome-preview-subject">{previewSubject}</span></p>
              </div>
              <div className="px-5 py-4 text-sm leading-relaxed text-slate-800" style={{ fontFamily: "Helvetica, Arial, sans-serif" }} data-testid="settings-coach-welcome-preview-body">
                <p className="whitespace-pre-wrap">
                  {previewParts.map((part, i) => (
                    <React.Fragment key={i}>
                      {part}
                      {i < previewParts.length - 1 && (
                        <a href={SAMPLE.login_url} className="text-sky-600 underline underline-offset-2" target="_blank" rel="noreferrer">{SAMPLE.login_url}</a>
                      )}
                    </React.Fragment>
                  ))}
                </p>
              </div>
            </div>
            <p className="mt-2 text-xs text-slate-400">Sample name (Jordan Smith) and your login link are shown in place of the {"{first_name}"} / {"{last_name}"} / {"{login_url}"} tags.</p>
          </div>
        )}
      </div>
    </CollapsibleCard>
  );
}

function TestEmailCard({ senderEmail }) {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => { if (user?.email) setEmail(user.email); }, [user]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/test-email", { email });
      if (data.status === "sent") toast.success(data.detail);
      else if (data.status === "disabled" || data.status === "not_configured") toast.warning(data.detail);
      else toast.error(data.detail || "Send failed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <Card className="max-w-2xl border-slate-200 p-6 shadow-sm" data-testid="settings-test-email-card">
      <div className="flex gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
          <Send className="h-5 w-5" />
        </span>
        <div className="flex-1">
          <Label className="text-base font-semibold text-slate-800">Send a test email</Label>
          <p className="mt-1 text-sm text-slate-500">
            Confirm real delivery. Requires the Email toggle above to be on. Sends a branded test message to the address below.
          </p>
          {senderEmail ? (
            <p className="mt-2 text-xs text-slate-400" data-testid="settings-sender-email">
              Sending from <span className="font-semibold text-slate-600">{senderEmail}</span>
            </p>
          ) : null}
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" className="sm:flex-1" data-testid="settings-test-email-input" />
            <Button onClick={send} disabled={sending || !email} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="settings-test-email-send">
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send test
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

function SectionTitle({ title, subtitle, testId }) {
  return (
    <div className="pt-2" data-testid={testId}>
      <h2 className="font-heading text-lg font-semibold text-slate-900">{title}</h2>
      <p className="text-sm text-slate-500">{subtitle}</p>
    </div>
  );
}

// Feature Adoption on the dashboard deep-links here: /admin/settings?focus=licenses
function useFocusLicenses(ready) {
  const [params] = useSearchParams();
  const focus = params.get("focus") === "licenses";
  useEffect(() => {
    if (!focus || !ready) return undefined;
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="settings-license-management-card"]');
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("ring-2", "ring-sky-400", "ring-offset-2");
      setTimeout(() => el.classList.remove("ring-2", "ring-sky-400", "ring-offset-2"), 2500);
    }, 200);
    return () => clearTimeout(t);
  }, [focus, ready]);
  return { focus, plan: params.get("plan") || "" };
}

export default function AdminSettings() {
  const [settings, setSettings] = useState({ email_enabled: false, web_push_enabled: false, sms_enabled: false, sms_configured: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const focusLicenses = useFocusLicenses(!loading);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setSettings(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (key, label) => async (value) => {
    const prev = settings;
    setSettings((s) => ({ ...s, [key]: value }));
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", { [key]: value });
      setSettings(data);
      toast.success(`${label} ${value ? "turned on" : "turned off"}`);
    } catch (err) {
      setSettings(prev);
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const busy = loading || saving;

  return (
    <div>
      <PageHeader title="Settings" subtitle="Communication channels and app-wide features" />
      <div className="space-y-5">
        <SectionTitle title="Functions" subtitle="How RallyPointGo reaches people. In-app notifications always work." testId="settings-functions-section" />
        <ToggleCard
          icon={Mail} testId="email" title="Email notifications"
          description={`When on, RallyPointGo emails alerts (signups, approvals, submissions, broadcasts) from ${settings.sender_email || "your configured sender address"}. In-app notifications are unaffected.`}
          value={!!settings.email_enabled} onToggle={toggle("email_enabled", "Email notifications")} disabled={busy}
          statusOn="Emails are being sent" statusOff="Emails are paused"
        />
        <ToggleCard
          icon={BellRing} testId="push" title="Push notifications"
          description="Sends browser/app push alerts to members who have opted in on their device. Great for events, conferences and announcements — even when the app is closed."
          value={!!settings.web_push_enabled} onToggle={toggle("web_push_enabled", "Push notifications")} disabled={busy}
          statusOn="Push alerts are being sent" statusOff="Push alerts are paused"
        />
        <ToggleCard
          icon={MessageSquare} testId="sms" title="SMS text messages"
          description="Texts members who have a phone number saved in their profile (via Twilio). Ideal for time-sensitive alerts."
          value={!!settings.sms_enabled} onToggle={toggle("sms_enabled", "SMS notifications")} disabled={busy || !settings.sms_configured}
          statusOn="Text messages are being sent" statusOff="Text messages are paused"
          note={settings.sms_configured ? null : "Twilio is not set up yet — add your Twilio keys to start sending texts. The toggle stays locked until then."}
        />
        <TestEmailCard senderEmail={settings.sender_email} />

        <SectionTitle title="Features" subtitle="Master switches for every team. Licensed features also need to be in each team's plan (License Management below)." testId="settings-features-section" />
        <FeatureSwitches settings={settings} toggle={toggle} busy={busy} />

        <WelcomeEmailCard />
        <LicenseManagement defaultOpen={focusLicenses.focus} initialPlan={focusLicenses.plan} />
      </div>
    </div>
  );
}
